import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Plus,
  List,
  Search,
  MoreHorizontal,
  Download,
  Trash2,
} from "lucide-react";
import { DataGenieIcon } from "src/components/icons";
import PageLayout from "../common/PageLayout";
import PageTitle from "../common/PageTitle";
import NoAccessPage from "../common/noAccessPage";
import { NavLink, useNavigate } from "react-router-dom";
import LinkButton from "../core/LinkButton";
import { formatDate } from "../../utils/dates";
import {
  UPLOAD_STATUSES,
  UPLOAD_STATUSES_CONFIG,
} from "../../constants/upload";
import createLoopingArray from "src/utils/createLoopingArray";
import Button from "../core/Button";
import { toast } from "src/components/shadcn/useToast";
import { useGetCustomUploadsQuery, useDeleteCustomUploadMutation } from "src/hooks/useCustomUploads";
import { convertFileToJson } from "./utils";
import Pagination from "../SearchListings/Pagination";
import PageSize from "../SearchListings/PageSize";
import TableSkeleton from "../core/TableSkeleton";
import SearchSummary from "../SearchListings/SearchSummary";
import { STEPS, LOOKUP_TYPES } from "./constants";
import useIsMobile from "src/hooks/useIsMobile";
import { useFeatureDiscovery } from "src/hooks/useFeatureDiscovery";
import FeatureDiscoveryTooltip from "../FeatureDiscoveryTooltip";
import { useAuth } from "../utils/auth/assist/authContext";
import LookupStatusIndicator, { LookupStatus } from "../common/LookupStatusIndicator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../core/Table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shadcn/DropDownMenu";

const getImportLookupStatus = (status) => {
  if (
    [UPLOAD_STATUSES.insufficientcredits, UPLOAD_STATUSES.error].includes(
      status
    )
  ) {
    return LookupStatus.Error;
  } else if (status === UPLOAD_STATUSES.complete) {
    return LookupStatus.Completed;
  } else {
    return LookupStatus.Busy;
  }
};

const getStatusLabel = (status) => {
  return (
    UPLOAD_STATUSES_CONFIG.find(({ name }) => status === name)?.label || "--"
  );
};

const getLookupTypeLabel = (searchType) => {
  switch (searchType) {
    case LOOKUP_TYPES.reverseAddress:
      return "Address";
    case LOOKUP_TYPES.reversePhone:
      return "Phone";
    default:
      return "--";
  }
};

const PreviousImports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const isMobile = useIsMobile();
  const [activeDownloads, setActiveDownloads] = useState([]);
  const [deletingItems, setDeletingItems] = useState([]);
  const { data, refetch, isLoading } = useGetCustomUploadsQuery({
    pageSize,
    currentPage,
  });
  const deleteUploadMutation = useDeleteCustomUploadMutation();

  const features = useMemo(
    () => [
      "search-imported-listings",
      "view-imported-listings",
      "download-imported-listings",
    ],
    []
  );

  const { currentFeature, nextFeature, isLastFeature, shouldShowTooltip } =
    useFeatureDiscovery(features);

  const handleRowClick = useCallback((row) => {
    navigate(`/data-genie/search?lookupIds=${row.id}&fromDate=&toDate=`);
  }, [navigate]);

  const handleDeleteLookup = useCallback(async (lookupId, lookupName) => {
    if (!window.confirm(`Are you sure you want to delete "${lookupName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingItems((prev) => [...prev, lookupId]);

    deleteUploadMutation.mutate(lookupId, {
      onSuccess: () => {
        setDeletingItems((prev) => prev.filter((id) => id !== lookupId));
      },
      onError: (error) => {
        console.error("Failed to delete lookup:", error);
        toast({
          title: "Error",
          description: (
            <>
              Failed to delete <strong>{lookupName}</strong>. Please try again.
            </>
          ),
          variant: "destructive",
        });
        setDeletingItems((prev) => prev.filter((id) => id !== lookupId));
      },
    });
  }, [deleteUploadMutation]);


  const handleDownload = useCallback((row) => {
    setActiveDownloads((prev) => [...prev, row.id]);

    // Create a temporary link to trigger download
    const link = document.createElement("a");
    link.href = `/api/customupload/listing/csv?list_id=${row.id}`;
    link.download = row.name || "download.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Remove from active downloads after a short delay
    setTimeout(() => {
      setActiveDownloads((prev) => prev.filter((id) => id !== row.id));
    }, 1000);
  }, []);

  const createActionsMenu = useCallback(
    (row, isNeedsTooltip) => {
      const isErrorState = [UPLOAD_STATUSES.insufficientcredits, UPLOAD_STATUSES.error].includes(row.status);
      const isCompleted = row.status === UPLOAD_STATUSES.complete;

      const menu = (
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                className="gap-3 cursor-pointer focus:outline-none"
                onClick={() => handleRowClick(row)}
              >
                <List className="h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem
                className="gap-3 cursor-pointer focus:outline-none"
                onClick={() => handleDownload(row)}
                disabled={activeDownloads.includes(row.id) || !isCompleted}
              >
                <Download className="h-4 w-4" />
                {activeDownloads.includes(row.id) ? "Downloading..." : "Download"}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="gap-3 cursor-pointer focus:outline-none"
                onClick={() => handleDeleteLookup(row.id, row.name)}
                disabled={deletingItems.includes(row.id)}
              >
                <Trash2 className="h-4 w-4" />
                {deletingItems.includes(row.id) ? "Deleting..." : "Delete"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );

      return isNeedsTooltip ? (
        <FeatureDiscoveryTooltip
          content="Download or delete this lookup!"
          isOpen={shouldShowTooltip("download-imported-listings")}
          onNext={nextFeature}
          isLast={isLastFeature}
        >
          {menu}
        </FeatureDiscoveryTooltip>
      ) : (
        menu
      );
    },
    [
      handleDownload,
      handleDeleteLookup,
      activeDownloads,
      deletingItems,
      shouldShowTooltip,
      nextFeature,
      isLastFeature,
    ]
  );

  useEffect(() => {
    const interval = setInterval(refetch, 10000); // 10000 milliseconds = 10 seconds
    // Clean up the interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const startNewLookupButton = (
    <LinkButton to="/data-genie/start" icon={<Plus />}>
      Start Lookup
    </LinkButton>
  );

  const previousImports = useMemo(() => data?.imports || [], [data]);
  const totalRecords = useMemo(() => data?.totalRecords || 0, [data]);

  return (
    <PageLayout zeroMargin={false} suppressTopPadding>
      <PageTitle
        titleIcon={<DataGenieIcon style={{ fontSize: "19px" }} />}
        title="Previous Lookups"
        subtitle="Data Genie"
        adjacentContent={
          <SearchSummary
            totalRecords={totalRecords}
            pageSize={pageSize}
            currentPage={currentPage}
            isShowing={!isLoading && totalRecords}
          />
        }
        rightSideContent={
          <div className="flex gap-2">
            <FeatureDiscoveryTooltip
              content="Search your previously imported listings!"
              isOpen={shouldShowTooltip("search-imported-listings")}
              onNext={nextFeature}
              isLast={isLastFeature}
            >
              <LinkButton
                variant="secondary"
                to="/data-genie/search"
                icon={<Search />}
              >
                Imported Contacts
              </LinkButton>
            </FeatureDiscoveryTooltip>
            {startNewLookupButton}
          </div>
        }
      />

      {!!user?.customupload ? (
        <section>
          {isLoading && (
            <TableSkeleton rows={isMobile ? 6 : 12} fileColumnsKeys={7} />
          )}
          {!isLoading && !previousImports?.length && (
            <div className="flex flex-col justify-center items-center p-12 gap-3">
              <p className="text-gray-primary text-lg">No lookups yet</p>
              {startNewLookupButton}
            </div>
          )}
          {!isLoading && !!previousImports?.length && (
            <>
              <div className=" hidden md:block w-full overflow-auto mt-8 h-[700px]">
                <Table condensed="true">
                  <TableHeader className="sticky top-0 z-10">
                    <TableRow>
                      {[
                        "Name",
                        "File Name",
                        "Type",
                        "Status",
                        "Total Rows",
                        "Date Processed",
                        "Source",
                        "Actions",
                      ].map((heading, index) => (
                        <TableHead key={`previous-uploads-header-${index}`}>
                          {heading}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previousImports?.map((row, index) => (
                      <TableRow
                        key={`map-fields-${index}`}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleRowClick(row)}
                      >
                        <TableCell className="max-w-[300px] truncate">{row.name}</TableCell>
                        <TableCell>{row.file_name || "--"}</TableCell>
                        <TableCell>{getLookupTypeLabel(row.lookup_type)}</TableCell>
                        <TableCell>
                          <div className="inline-block">
                            <LookupStatusIndicator
                              status={getImportLookupStatus(row.status)}
                              label={getStatusLabel(row.status)}
                            />
                          </div>
                        </TableCell>
                        <TableCell>{row.total_records}</TableCell>
                        <TableCell>
                          {formatDate(row.create_timestamp) || "--"}
                        </TableCell>
                        <TableCell>{row.source}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {createActionsMenu(row, index === 0 && !isMobile)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Mobile */}
              <div className=" md:hidden flex flex-col gap-1 w-full overflow-x-auto mt-8  bg-slate-400 overscroll-none">
                {previousImports?.map((row, index) => (
                  <div
                    key={`map-fields-mobile-${index}`}
                    className="card min-h-36 w-full bg-white pt-2 pb-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleRowClick(row)}
                  >
                    <div className=" body flex justify-between px-2 gap-10">
                      <div className="flex row1 flex-col gap-3">
                        <div className=" justify-between gap-3  min-w-[40px]">
                          <p className="text-[14px] leading-5 text-gray-secondary ">
                            Name
                          </p>
                          <p className="text-[18px] font-semibold  leading-5 text-gray-primary ">
                            {row.name}
                          </p>
                        </div>
                        <div className=" justify-between gap-3  min-w-[40px]">
                          <p className="text-[14px]  leading-5 text-gray-secondary  ">
                            File Name
                          </p>
                          <p className="text-[18px] font-semibold  leading-5 text-gray-primary ">
                            {row.file_name || "--"}
                          </p>
                        </div>

                        <div className=" justify-between gap-3  min-w-[40px]">
                          <p className="text-[14px] leading-5 text-gray-secondary  ">
                            Type
                          </p>
                          <p className="text-[18px] font-semibold  leading-5 text-gray-primary ">
                            {getLookupTypeLabel(row.lookup_type)}
                          </p>
                        </div>

                        <div className=" justify-between gap-3  min-w-[40px]">
                          <p className="text-[14px] font-semibold leading-5 text-gray-secondary  ">
                            Status
                          </p>
                          <div className="text-[18px] text-left font-semibold  leading-5 text-gray-primary ">
                            <div className="inline-block">
                              <LookupStatusIndicator
                                status={getImportLookupStatus(row.status)}
                                label={getStatusLabel(row.status)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex row2 flex-col gap-3">
                        <div className=" justify-between gap-3  min-w-[40px]">
                          <p className="text-[14px] leading-5 text-gray-secondary  ">
                            Total Rows
                          </p>
                          <p className="text-[18px] font-semibold  leading-5 text-gray-primary ">
                            {row.total_records}
                          </p>
                        </div>
                        <div className=" justify-between gap-3  min-w-[40px]">
                          <p className="text-[14px]  leading-5 text-gray-secondary  ">
                            Date Added
                          </p>
                          <p className="text-[18px] font-semibold  leading-5 text-gray-primary ">
                            {row.complete_date}
                          </p>
                        </div>

                        <div className=" justify-between gap-3  min-w-[40px]">
                          <p className="text-[14px]leading-5 text-gray-secondary  ">
                            Source
                          </p>
                          <p className="text-[18px] font-semibold  leading-5 text-gray-primary ">
                            {row.source}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className=" flex justify-between mt-4">
                      <LinkButton
                        variant="secondary"
                        icon={<List />}
                        to={`/data-genie/search?lookupIds=${row.id}&fromDate=&toDate=`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        View
                      </LinkButton>
                      <div onClick={(e) => e.stopPropagation()}>
                        {createActionsMenu(row, index === 0 && isMobile)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-4">
                <PageSize
                  value={String(pageSize)}
                  onChange={(value) => {
                    setPageSize(Number(value));
                    setCurrentPage(1);
                  }}
                />
                <Pagination
                  numRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={(updatedPage) => {
                    setCurrentPage(updatedPage);
                  }}
                />
              </div>
            </>
          )}
        </section>
      ) : (
        <NoAccessPage product="Data Genie" />
      )}
    </PageLayout>
  );
};

export default PreviousImports;
